import React from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { DateObject } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { PatientStaticDocumentKey, PatientStaticDocument } from '@uplift/api';
import { useUserQuery } from '@uplift/queries';
import { useTimezone } from '@uplift/contexts';


const documentResolver = (name: string) => import(`./markdown/${name}.document.mdx`);
const Acknowledged: React.VFC<{
  firstName: string;
  lastName: string;
  datetime: DateObject;
}> = ({ firstName, lastName, datetime: _datetime }) => {
  const timezone = useTimezone();
  const datetime = React.useMemo(() => _datetime.tz(timezone), [ _datetime, timezone ]);

  return (
    <Typography style={{ fontWeight: 500 }}>
      Acknowledged by {firstName} {lastName}, {datetime.format(DateFormats['M/D/YY'])} at {datetime.format('hh:mm a z')}
    </Typography>
  );
};


export const documentsMapping: Record<PatientStaticDocumentKey, { title: string; component: React.ElementType }> = {
  [PatientStaticDocumentKey.financialResponsibility]: {
    title: 'Financial Responsibility',
    component: React.lazy(() => documentResolver('FinancialResponsibility')),
  },
  [PatientStaticDocumentKey.privacyPolicy]: {
    title: 'Privacy Policy',
    component: React.lazy(() => documentResolver('PrivacyPolicy')),
  },
  [PatientStaticDocumentKey.consentToTreat]: {
    title: 'Consent to Treat',
    component: React.lazy(() => documentResolver('ConsentToTreat')),
  },
};


const useStyles = makeStyles()(theme => ({
  root: {
    '& ul, & ol': {
      paddingLeft: theme.spacing(2),
    }
  }
}));


export type PatientStaticDocumentProps = {
  document: PatientStaticDocument;
  acknowledgedAt: DateObject;
};


export const PatientStaticDocumentComponent: React.FC<PatientStaticDocumentProps> = (props) => {
  const { classes } = useStyles();
  const { data: patient } = useUserQuery(props.document.patientId);
  const document = documentsMapping[props.document.documentKey];

  if (document) {
    const Component = document.component;

    return (
      <Box>
        <Typography component="div" className={classes.root}>
          <Component />
        </Typography>

        <Acknowledged
          firstName={patient?.firstName ?? ''}
          lastName={patient?.lastName ?? ''}
          datetime={props.acknowledgedAt}
        />
      </Box>
    );
  }


  return (
    <div>Unknown document</div>
  );
};
